/* eslint-disable @typescript-eslint/no-explicit-any */
/** --------------------------------------------------------
 * Author Tien Tran
 * Email tientran0019@gmail.com
 * Phone 0972970075
 *
 * Created: 2024-05-23 16:45:58

 * Last updated on: 2024-05-23 16:45:58
 * Last updated by: Tien Tran
 *------------------------------------------------------- */
import axios from "axios";
import { v4 as uuidv4 } from "uuid";

import { toast } from "@onair/minimal-ui/snackbar";
import { toNonAccentVietnamese } from "./helper.ts";

const baseUrl = `${process.env.NEXT_PUBLIC_REST_API}/api/v2/upload`;

const generateUniqueFileName = (
	originalFileName: string,
	userId?: string,
	resourceId?: string,
): string => {
	const fileExtension = originalFileName.split(".").pop(); // Get the file extension
	const fileName = toNonAccentVietnamese(
		originalFileName.split(".").slice(0, -1).join("."),
	);

	if (userId && resourceId) {
		const uniqueNameParts = [userId, resourceId, uuidv4()];

		return `${uniqueNameParts.join("_")}.${fileExtension}`.trim(); // Combine and return
	}

	return `${fileName}-${Math.floor(Date.now() / 1000)}.${fileExtension}`.trim();
};

const generateUniqueFileName2 = (
	originalFileName: string,
	userId?: string,
	resourceId?: string,
): string => {
	const fileExtension = originalFileName.split(".").pop(); // Get the file extension

	if (userId && resourceId) {
		const uniqueNameParts = [userId, resourceId, uuidv4()];

		return `${uniqueNameParts.join("/")}.${fileExtension}`.trim(); // Combine and return
	}

	return `${uuidv4()}.${fileExtension}`.trim();
};

const getPresignedUrl = async (
	fileName: string,
	isVideo = false,
	expiredIn: number,
	bucket?: string,
) => {
	const response = await axios.post(`${baseUrl}/sign-url`, {
		key: fileName,
		isVideo,
		expiredIn,
		...(bucket && { bucket }),
	});
	return response.data;
};

const uploadFileToS3 = async (
	uploadUrl: string,
	file: File,
	onProgress?: (progress: number) => void,
	abortController?: AbortController,
) => {
	try {
		await axios.put(uploadUrl, file, {
			headers: { "Content-Type": file.type },
			signal: abortController?.signal,
			onUploadProgress: (event) => {
				if (onProgress) {
					const progress = Math.round((event.loaded / event.total) * 100);
					onProgress(progress);
				}
			},
		});
	} catch (error) {
		if (axios.isCancel(error)) {
			throw new Error("Upload cancelled");
		} else {
			throw error; // Re-throw other errors
		}
	}
};

export const getVideoDuration = (file: File): Promise<number> => {
	return new Promise((resolve, reject) => {
		if (!file.type.startsWith("video/")) {
			resolve(0); // Not a video file, return 0 duration
			return;
		}

		const video = document.createElement("video");
		video.preload = "metadata";

		video.onloadedmetadata = () => {
			URL.revokeObjectURL(video.src); // Clean up the object URL
			resolve(video.duration); // Duration in seconds
		};

		video.onerror = () => {
			reject(new Error("Unable to retrieve video duration."));
		};

		video.src = URL.createObjectURL(file);
	});
};

const getFileUrl = async (
	fileName: string,
	bucket?: string,
): Promise<string> => {
	const response = await axios.get(`${baseUrl}/sign-url`, {
		params: { key: fileName, ...(bucket && { bucket }) },
	});
	return response.data;
};

export const isVideoFile = (file: File): boolean => {
	return file.type.startsWith("video/");
};

export const uploadToS3 = async (
	fileArray?: File[] | undefined | null,
	bucket?: string,
	userId?: string,
	resourceId?: string,
	onProgress?: (progress: number) => void,
	abortController?: AbortController,
) => {
	try {
		if (!fileArray || fileArray.length === 0) {
			return [];
		}

		const uploadResults = await Promise.allSettled(
			fileArray.map(async (file) => {
				const duration = await getVideoDuration(file);
				console.log(`File: ${file.name}, Duration: ${duration} seconds`);

				const fileName = generateUniqueFileName(file.name, userId, resourceId);
				const isVideo = isVideoFile(file);

				const expiredIn = 60;

				const uploadUrl = await getPresignedUrl(
					fileName,
					isVideo,
					expiredIn,
					bucket,
				);
				await uploadFileToS3(uploadUrl, file, onProgress, abortController);
				const fileUrl = await getFileUrl(fileName, bucket);

				if (userId && resourceId) {
					// Temperate before having new update
					return { fileUrl, fileName };
				}
				return fileUrl;
			}),
		);

		// Filter and return successfully uploaded images
		return uploadResults
			.filter(({ status }) => status === "fulfilled")
			.map((result) => {
				console.log({ result });
				return result.value;
			});
	} catch (error: any) {
		const massage =
			error.response?.data?.message ||
			error.message ||
			"Tệp tải lên đã xảy ra lỗi, vui lòng thử lại sau.";

		if (abortController?.signal.aborted) {
			toast.info("Upload cancelled.");
		}

		if (process.env.NEXT_PUBLIC_APP_ENV !== "production") {
			console.error("DEV ~ Upload Error:", {
				massage,
				path: error.request?.path,
			});
		}

		if (typeof window !== "undefined") {
			toast.dismiss(error.request?.path);
			toast.error(massage, {
				id: error.request?.path ?? "--",
			});
		}

		throw new Error(massage);
	}
};

export const uploadToS3V2 = async (
	fileArray: {
		file: File;
		fileName: string;
	}[],
	bucket?: string,
	onProgress?: (progress: number) => void,
	abortController?: AbortController,
) => {
	try {
		if (!fileArray || fileArray.length === 0) {
			return [];
		}

		const uploadResults = await Promise.allSettled(
			fileArray.map(async ({ file, fileName }) => {
				const duration = await getVideoDuration(file);
				console.log(`File: ${file.name}, Duration: ${duration} seconds`);

				const isVideo = isVideoFile(file);

				const expiredIn = 60;

				const uploadUrl = await getPresignedUrl(
					fileName,
					isVideo,
					expiredIn,
					bucket,
				);
				await uploadFileToS3(uploadUrl, file, onProgress, abortController);
				const fileUrl = await getFileUrl(fileName, bucket);
				return { fileUrl, fileName };
			}),
		);
		// Filter and return successfully uploaded images
		return uploadResults
			.filter(({ status }) => status === "fulfilled")
			.map((result) => {
				return result.value;
			});
	} catch (error: any) {
		const massage =
			error.response?.data?.message ||
			error.message ||
			"Tệp tải lên đã xảy ra lỗi, vui lòng thử lại sau.";

		if (abortController?.signal.aborted) {
			toast.info("Upload cancelled.");
		}

		if (process.env.NEXT_PUBLIC_APP_ENV !== "production") {
			console.error("DEV ~ Upload Error:", {
				massage,
				path: error.request?.path,
			});
		}

		if (typeof window !== "undefined") {
			toast.dismiss(error.request?.path);
			toast.error(massage, {
				id: error.request?.path ?? "--",
			});
		}

		throw new Error(massage);
	}
};

export const normalizeExt = (ext: string | undefined | null) => {
	if (!ext) return "";
	if (ext === "jpeg") return "jpg";
	return ext.toLowerCase();
};
